<template>
  <div class="is-shallow">
    <DefaultPanel :collapsed="true" :loading="isLoading" @toggle="onToggle">
      <template #header>
        <div class="is-flex is-align-items-center">
          <span class="icon is-small mr-2">
            <i class="fa-solid fa-share-alt" />
          </span>
          <span>Lineage Graph</span>
        </div>
      </template>
      <template #header-controls>
        <IconButton
          icon="fa-solid fa-external-link-alt"
          tooltip="Open lineage in new tab"
          size="small"
          @click.stop="openLineagePage"
        />
        <IconButton
          v-if="lineageData"
          icon="fa-solid fa-expand-arrows-alt"
          tooltip="Expand lineage"
          size="small"
          @click.stop="isModalOpen = true"
        />
      </template>
      <div v-if="error" class="has-text-danger">
        <span class="icon">
          <i class="fa-solid fa-exclamation-triangle" />
        </span>
        <span>Failed to load lineage data.</span>
      </div>
      <div v-else-if="lineageData && !isModalOpen" class="panel-graph">
        <LineageGraph :data="lineageData" :id-active="uuid" />
      </div>
    </DefaultPanel>

    <ModalCard
      v-if="isModalOpen"
      :handle-close="() => (isModalOpen = false)"
      height="95%"
      width="95%"
    >
      <template #header>
        <div>
          <span class="icon is-small mr-2">
            <i class="fa-solid fa-share-alt fa-xs" />
          </span>
          <span>Lineage Graph</span>
        </div>
      </template>
      <template #body>
        <div class="modal-graph">
          <LineageGraph :data="lineageData" :id-active="uuid" />
        </div>
      </template>
      <template #footer>
        <div>
          <button class="button is-outlined" @click="isModalOpen = false">
            <span class="icon">
              <i class="fa-solid fa-times" />
            </span>
            <span>Close</span>
          </button>
        </div>
      </template>
    </ModalCard>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'

import { get } from '@/api'
import { filterLineageModels } from '@/utils/lineage'

import DefaultPanel from '@/components/DefaultPanel.vue'

export default {
  name: 'LineageGraphPanel',
  components: {
    DefaultPanel,
    LineageGraph: defineAsyncComponent(
      () => import('@/components/LineageGraph.vue'),
    ),
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      error: false,
      lineageData: null,
      isModalOpen: false,
    }
  },
  methods: {
    onToggle(isExpanded) {
      if (isExpanded && !this.lineageData && !this.error) {
        this.fetchLineage()
      }
    },
    async fetchLineage() {
      this.isLoading = true
      this.error = false
      try {
        const res = await get(`resolver/${this.uuid}/lineage/?format=json`)
        this.lineageData = filterLineageModels(res.data.results)
      } catch {
        this.error = true
      } finally {
        this.isLoading = false
      }
    },
    openLineagePage() {
      const routeData = this.$router.resolve({
        path: `/lineage/${this.uuid}`,
      })
      window.open(routeData.href, '_blank')
    },
  },
}
</script>

<style>
/* Unscoped: ModalCard portals outside the scoped tree.
   Make its .p-2 body wrapper a flex column so .modal-graph can fill it. */
.modal-card-body > .p-2:has(> .modal-graph) {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
</style>

<style scoped>
/* Fixed height for the in-page panel — the panel sits in a scrollable
   page so it needs a concrete height to constrain the Cytoscape canvas. */
.panel-graph {
  height: 500px;
  display: flex;
  flex-direction: column;
}

.modal-graph {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
</style>
